import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import apiClient from "../api/apiClient";
import useChatStore from "../store/chatStore";
import useAuthStore from "../store/authStore";

function EmployeeSidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false); // ✅ added

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", String(collapsed));
    window.dispatchEvent(new CustomEvent("sidebar_toggle", { detail: { collapsed } }));
  }, [collapsed]);
  const profilePhoto = useAuthStore(state => state.profilePhoto);

  const navigate = useNavigate();
  const location = useLocation();

  const unreadCounts = useChatStore(state => state.unreadCounts || {});
  const totalUnreadChats = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  /* ✅ Close mobile on route change */
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  /* ✅ Close on resize */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ── Fetch profile ── */
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const employeeId = useAuthStore.getState().employeeId;
        if (!employeeId) return;
        const res = await apiClient.get(`/accounts/api/users/${employeeId}/`);
        const user = res.data.user || res.data;
        if (user?.photo) {
          const photo = user.photo;
          const photoPath = photo.startsWith("http")
            ? photo
            : photo.startsWith("/")
              ? photo
              : `/media/${photo}`;
          useAuthStore.getState().setProfilePhoto(photoPath);
        } else {
          useAuthStore.getState().setProfilePhoto(null);
        }
      } catch {
        useAuthStore.getState().setProfilePhoto(null);
      }
    };
    fetchProfile();
  }, []);

  /* ── Chat Unread Counts Sync ── */
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await apiClient.get("/chat/api/messages/unread-counts/");
        if (res.data?.unread_counts) {
          useChatStore.getState().setUnreadCounts(res.data.unread_counts);
        }
      } catch { /* silent */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 10000); // sync every 10s
    return () => clearInterval(id);
  }, []);

  /* ── Logout ── */
  const handleLogout = async () => {
    try {
      await apiClient.post("/accounts/api/logout/");
    } catch { }
    useAuthStore.getState().clearAuth();
    navigate("/login");
  };

  const handleResetPassword = () => navigate("/reset-password");

  /* ── Sidebar content ── */
  const SidebarContent = ({ isDrawer = false }) => (
    <div className="flex flex-col h-full">
      <div className="flex flex-col items-center pt-8 pb-6">
        {isDrawer && (
          <div className="w-full flex justify-end px-4 mb-2">
            <button
              onClick={() => setMobileOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div
          onClick={() => !isDrawer && setCollapsed(!collapsed)}
          className={`
            rounded-full border-2 border-white/30
            bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-600
            overflow-hidden shadow-lg hover:shadow-2xl
            transition-all duration-500 hover:scale-105 hover:border-white/50
            ${!isDrawer && collapsed ? "w-12 h-12 cursor-pointer" : "w-24 h-24"}
          `}
        >
          <img
            src={profilePhoto}
            alt="Employee Avatar"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = "/images/avatar.png"; }}
          />
        </div>

        {(isDrawer || !collapsed) && (
          <div className="mt-4 text-center px-2">
            <p className="text-base font-semibold text-white tracking-wide">Employee</p>
            <p className="text-xs text-white/70 mt-1">Project Workspace</p>
          </div>
        )}
      </div>

      <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

      <nav className="flex flex-col gap-2 px-3 py-5 flex-1 overflow-y-auto">
        <SidebarLink
          to="/employee/dashboard"
          label="Dashboard"
          collapsed={!isDrawer && collapsed}
          onClick={isDrawer ? () => setMobileOpen(false) : undefined}
        >
          <HomeIcon />
        </SidebarLink>

        <SidebarLink
          to="/employee/assigned-projects"
          label="My Time Sheet"
          collapsed={!isDrawer && collapsed}
          onClick={isDrawer ? () => setMobileOpen(false) : undefined}
          customActive={(loc) => {
            return loc.pathname === "/employee/assigned-projects" || loc.pathname === "/employee/progress";
          }}
        >
          <GridIcon />
        </SidebarLink>


        <SidebarLink
          to="/employee/activity"
          label="Employee Activity"
          collapsed={!isDrawer && collapsed}
          onClick={isDrawer ? () => setMobileOpen(false) : undefined}
        >
          <ClipboardIcon />
        </SidebarLink>

        <SidebarLink
          to="/employee/chat"
          label="Messages"
          collapsed={!isDrawer && collapsed}
          onClick={isDrawer ? () => setMobileOpen(false) : undefined}
          badge={totalUnreadChats}
        >
          <ChatIcon />
        </SidebarLink>

      </nav>

      <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

      <div className="p-3 flex flex-col gap-2.5">
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-3 bg-gradient-to-r from-red-600 to-red-700 py-2.5 rounded-xl"
        >
          <LogoutIcon />
          {(isDrawer || !collapsed) && <span>Log Out</span>}
        </button>

        <button
          onClick={handleResetPassword}
          className="flex items-center justify-center gap-3 border-2 border-white/40 py-2.5 rounded-xl"
        >
          <LockIcon />
          {(isDrawer || !collapsed) && <span>Reset Password</span>}
        </button>
      </div>
    </div>
  );

  const sidebarBase = `
    bg-gradient-to-b from-white/10 via-white/5 to-transparent
    backdrop-blur-md border-r border-white/20
    text-white shadow-2xl flex flex-col
  `;

  return (
    <>
      {/* ✅ MOBILE BUTTON */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-[60] w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-md border border-white/20 text-white shadow-lg"
      >
        {mobileOpen ? "✕" : "☰"}
      </button>

      {/* ✅ OVERLAY */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Desktop */}
      <aside className={`hidden lg:flex fixed top-0 left-0 h-screen z-50 ${collapsed ? "w-20" : "w-[260px]"} ${sidebarBase}`}>
        <SidebarContent />
      </aside>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.28 }}
            className={`lg:hidden fixed top-0 left-0 h-screen z-[56] w-72 ${sidebarBase}`}
          >
            <SidebarContent isDrawer />
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

/* ── SidebarLink ── */
function SidebarLink({ to, label, children, collapsed, onClick, badge, customActive }) {
  const location = useLocation();
  const isActive = customActive
    ? customActive(location)
    : location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium
        transition-all duration-300 active:scale-95
        hover:bg-white/15 relative
        ${isActive ? "bg-white/25 shadow-lg border border-white/30" : "border border-transparent"}
        ${collapsed ? "justify-center" : ""}
      `}
    >
      <div className={`relative flex-shrink-0 ${isActive ? "scale-110" : ""}`}>
        {children}
        {badge > 0 && collapsed && (
          <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white bg-red-500 rounded-full border border-white/30 animate-pulse">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      {!collapsed && <span className="truncate flex-1">{label}</span>}
      {badge > 0 && !collapsed && (
        <span className="ml-auto flex items-center justify-center min-w-[22px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </Link>
  );
}

/* ── Icons (UNCHANGED) ── */
function HomeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M3 12l9-9 9 9v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />
    </svg>
  );
}
function GridIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}
function LogoutIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M10 17l5-5-5-5v4H3v2h7v4z" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}


function ClipboardIcon() {
  return (
    <svg
      className="w-5 h-5 flex-shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  );
}


function ChatIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export default EmployeeSidebar;